import { Checkbox as MUICheckbox, CheckboxProps } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import React, { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { default as CheckBoxIcon } from '../../icons/Checkbox';
import CheckboxChecked from '../../icons/CheckboxChecked';
import CheckboxIndeterminate from '../../icons/CheckboxIndeterminate';
import { ColorKeys, palette } from '../../theme/colors';
import { SolvThemeType } from '../../theme/theme';
import Box from '../Box/Box';
import Text from '../Text/Text';

/** See:
 * https://material-ui.com/components/checkboxes/
 * https://mui.com/api/checkbox/
 **/

type CheckboxTone = 'default' | 'premium' | 'whiteLabel';

type ColorMap = {
  empty: string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  label: string;
  sublabel: string;
};

export const getCheckboxToneColors = (colors: {
  [key in ColorKeys]: string;
}): Record<CheckboxTone, ColorMap> => ({
  default: {
    empty: palette.white,
    label: palette.gray[600],
    sublabel: palette.gray[500],
    ...palette.gray,
  },
  whiteLabel: {
    empty: palette.white,
    '300': palette.gray[300],
    '400': palette.gray[400],
    '500': colors['crunchberry-dark'],
    '600': colors['crunchberry-dark'],
    label: palette.gray[600],
    sublabel: palette.gray[500],
  },
  premium: {
    empty: palette.white,
    '300': palette.teal.light,
    '400': palette.teal.base,
    '500': palette.teal.medium,
    '600': palette.teal.dark,
    label: palette.teal.dark,
    sublabel: palette.teal.medium,
  },
});

type Props = {
  label?: ReactNode;
  subLabel?: ReactNode;
  tone?: CheckboxTone;
};

const StyledRootContainer = styled(Box)`
  label[class^='MuiFormControlLabel'] {
    margin-left: unset;
  }
`;

const StyledLabelHolder = styled.div`
  margin-left: 12px;
  line-height: 24px;
  &.small {
    margin-left: 11.5px;
  }
  ,
  &.small p {
    font-size: 14px;
    line-height: 20px;
  }
`;

const StyledCheckbox = styled(MUICheckbox)<{ $tone: ColorMap }>`
  &.indeterminate {
    color: ${({ $tone }) => $tone['600']};;
  },
  svg .checkbox-border {
    stroke-width: 1;
  },
  &.Mui-focusVisible svg {
    color: ${({ $tone }) => $tone['600']};;
  },
  &.Mui-focusVisible svg .checkbox-border {
    stroke-width: 2;
  },
  &.Mui-disabled svg {
    color: ${({ $tone }) => $tone['300']};;
  },
  [class^="MuiIconButton-label"] {
    width: auto;
  },
  & svg.small {
    width: 16px;
    height: 16px;
  },
  &:hover svg {
    color: ${({ $tone }) => $tone['500']};;
  },
`;

const StyledLabel = styled(Text)<{ $tone: ColorMap }>`
  color: ${({ $tone }) => $tone.label};
  &.disabled {
    color: ${({ $tone }) => $tone['400']};
  }
`;

const StyledSubLabel = styled(Text)<{ $tone: ColorMap }>`
  color: ${({ $tone }) => $tone.sublabel};
  &.disabled {
    color: ${({ $tone }) => $tone['400']};
  }
`;

const Checkbox = (props: CheckboxProps & Props) => {
  const { label, subLabel, tone = 'default', ...rest } = props;
  const rootClassName = 'solv-checkbox';
  const checkBoxClassName = props.indeterminate ? 'indeterminate' : undefined;
  const textClassName = props.disabled ? 'disabled' : undefined;

  const { colors } = useTheme() as SolvThemeType;
  const tonePalette = getCheckboxToneColors(colors)[tone];

  if (label) {
    const labelVariant = subLabel ? 'subtitle' : 'body';
    const labelProp = (
      <StyledLabelHolder className={props.size}>
        <StyledLabel
          $tone={tonePalette}
          variant={labelVariant}
          className={textClassName}
        >
          {label}
        </StyledLabel>
        {subLabel && (
          <StyledSubLabel
            $tone={tonePalette}
            className={textClassName}
            variant="body"
          >
            {subLabel}
          </StyledSubLabel>
        )}
      </StyledLabelHolder>
    );
    return (
      <StyledRootContainer py="9px" className={rootClassName}>
        <FormControlLabel
          control={
            <StyledCheckbox
              $tone={tonePalette}
              className={checkBoxClassName}
              checkedIcon={<CheckboxChecked color={tonePalette['600']} />}
              icon={
                <CheckBoxIcon
                  color={tonePalette['400']}
                  className={props.size}
                />
              }
              indeterminateIcon={
                <CheckboxIndeterminate color={tonePalette['600']} />
              }
              {...rest}
            />
          }
          label={labelProp}
        />
      </StyledRootContainer>
    );
  }
  return (
    <Box className={rootClassName}>
      <StyledCheckbox
        $tone={tonePalette}
        className={checkBoxClassName}
        checkedIcon={<CheckboxChecked color={tonePalette['600']} />}
        icon={
          <CheckBoxIcon color={tonePalette['400']} className={props.size} />
        }
        indeterminateIcon={<CheckboxIndeterminate color={tonePalette['600']} />}
        {...props}
      />
    </Box>
  );
};

Checkbox.displayName = 'Checkbox';
export default Checkbox;
